import React, { useCallback } from 'react'
import {
  MediaLibraryActionType,
  MediaLibraryActions,
} from '../providers/MLProvider'

export interface IdMap {
  [key: string]: (index: number) => string
}

export interface SetSelectedAssetMap {
  [key: string]: (v: Set<string>) => void
}

export interface UseGridKeyboardNavigationParams {
  gridOrder: string[]
  assetCountMap: Record<string, number>
  getIdMap: IdMap
  colCount: number
  selectedAssetMap: Record<string, Set<string>>
  setSelectedAssetMap: SetSelectedAssetMap
  focusedIndex: { index: number; type: string; id: string } | null
  setFocusedIndex: (
    v: { index: number; type: string; id: string } | null,
  ) => void
  assetRefMap: Record<string, React.RefObject<any>>
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  route: '/search' | '/media-library' | '/media-collections'
}

function useGridKeyboardNavigation({
  gridOrder,
  assetCountMap,
  getIdMap,
  colCount,
  selectedAssetMap,
  setSelectedAssetMap,
  focusedIndex,
  setFocusedIndex,
  assetRefMap,
  dispatch,
  route,
}: UseGridKeyboardNavigationParams) {
  // Debounce scroll-to-index to reliably focus item on rapid key presses
  const scheduledScrollRef = React.useRef<number | null>(null)
  // Throttle state
  const lastScrollAtRef = React.useRef<number>(0)
  const pendingTimeoutRef = React.useRef<number | null>(null)
  const throttleMs = 100

  const selectSingleAsset = useCallback(
    (gridType: string, newId: string) => {
      Object.entries(setSelectedAssetMap).forEach(([type, selected]) => {
        const t = type as string
        if (t === gridType) {
          selectedAssetMap[type] = new Set([newId])
          selected(selectedAssetMap[type])
        } else {
          selectedAssetMap[type].clear()
          selected(selectedAssetMap[type])
        }
      })
    },
    [setSelectedAssetMap, selectedAssetMap],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'a' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        setFocusedIndex(null)
        dispatch({ type: MediaLibraryActions.SELECT_ALL, payload: route })
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        Object.entries(setSelectedAssetMap).forEach(([type, selected]) => {
          const t = type as string
          selectedAssetMap[type].clear()
          selected(selectedAssetMap[type])
        })
        setFocusedIndex(null)
        return
      }

      if (focusedIndex === null) {
        return
      }
      const currentIndex = focusedIndex.index
      const currentId = focusedIndex.id
      const currentGrid = focusedIndex.type
      if (currentIndex === null) return
      let newIndex = -1
      let newId = ''

      let currentGridOrderIndex = gridOrder.indexOf(focusedIndex.type)
      let gridType: string = focusedIndex.type
      switch (event.key) {
        case 'ArrowLeft': {
          event.preventDefault()
          newIndex = currentIndex - 1
          while (currentGridOrderIndex > 0 && newIndex < 0) {
            currentGridOrderIndex--
            const prevGridType = gridOrder[currentGridOrderIndex]
            const prevGridCount = assetCountMap[prevGridType]
            if (prevGridCount <= 0) continue
            else {
              newIndex = prevGridCount - 1
              gridType = prevGridType
              break
            }
          }
          if (newIndex < 0) {
            return
          }
          newId = getIdMap[gridType](newIndex)
          if (event.shiftKey) {
            const newSet = selectedAssetMap[gridType]
            if (newSet.has(newId)) {
              if (newIndex === assetCountMap[gridType] - 1) {
                selectedAssetMap[currentGrid].delete(currentId)
                setSelectedAssetMap[currentGrid](selectedAssetMap[currentGrid])
              } else {
                newSet.delete(currentId)
              }
            } else {
              newSet.add(newId)
            }
            setSelectedAssetMap[gridType](newSet)
          } else {
            selectSingleAsset(gridType, newId)
          }
          setFocusedIndex({ index: newIndex, type: gridType, id: newId })
          break
        }
        case 'ArrowRight': {
          event.preventDefault()
          newIndex = currentIndex + 1

          while (
            currentGridOrderIndex < gridOrder.length - 1 &&
            newIndex >= assetCountMap[gridType]
          ) {
            currentGridOrderIndex++
            const nextGridType = gridOrder[currentGridOrderIndex]
            const nextGridCount = assetCountMap[nextGridType]
            if (nextGridCount <= 0) continue
            else {
              newIndex = 0
              gridType = nextGridType
              break
            }
          }
          if (newIndex >= assetCountMap[gridType]) {
            return
          }

          newId = getIdMap[gridType](newIndex)
          if (event.shiftKey) {
            const newSet = selectedAssetMap[gridType]
            if (newSet.has(newId)) {
              if (newIndex === 0) {
                selectedAssetMap[currentGrid].delete(currentId)
                setSelectedAssetMap[currentGrid](selectedAssetMap[currentGrid])
              } else {
                newSet.delete(currentId)
              }
            } else {
              newSet.add(newId)
            }
            setSelectedAssetMap[gridType](newSet)
          } else {
            selectSingleAsset(gridType, newId)
          }
          setFocusedIndex({ index: newIndex, type: gridType, id: newId })
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          newIndex = currentIndex - colCount
          const gridTypeList: string[] = [gridType]
          while (currentGridOrderIndex > 0 && newIndex < 0) {
            currentGridOrderIndex--
            const prevGridType = gridOrder[currentGridOrderIndex]
            const prevGridCount = assetCountMap[prevGridType]
            if (prevGridCount <= 0) continue
            else {
              const lastRowCount =
                prevGridCount % colCount === 0
                  ? colCount
                  : prevGridCount % colCount
              const currentColumnPosition =
                (currentIndex + 1) % colCount === 0
                  ? colCount
                  : (currentIndex + 1) % colCount

              if (lastRowCount >= currentColumnPosition) {
                newIndex =
                  prevGridCount - (lastRowCount - currentColumnPosition) - 1
                gridType = prevGridType
              } else if (prevGridCount > colCount) {
                newIndex =
                  prevGridCount -
                  lastRowCount -
                  colCount +
                  currentColumnPosition -
                  1
                gridType = prevGridType
              }
              gridTypeList.push(prevGridType)
            }
          }

          if (newIndex < 0) {
            return
          }
          newId = getIdMap[gridType](newIndex)
          if (event.shiftKey) {
            if (gridTypeList.length === 1) {
              const newSet = selectedAssetMap[gridType]
              if (newSet.has(newId)) {
                for (let i = newIndex + 1; i <= currentIndex; i++) {
                  newSet.delete(getIdMap[gridType](i))
                }
              } else {
                for (let i = newIndex; i < currentIndex; i++) {
                  newSet.add(getIdMap[gridType](i))
                }
              }
              setSelectedAssetMap[gridType](newSet)
            } else {
              const upcomingGrid = selectedAssetMap[gridType]
              if (upcomingGrid.has(newId)) {
                gridTypeList.forEach((type, idx) => {
                  if (idx === 0) {
                    const newSet = selectedAssetMap[type]
                    for (let i = 0; i <= currentIndex; i++) {
                      newSet.delete(getIdMap[type](i))
                    }
                    setSelectedAssetMap[type](newSet)
                  } else if (idx === gridTypeList.length - 1) {
                    const newSet = selectedAssetMap[type]
                    for (let i = newIndex + 1; i < assetCountMap[type]; i++) {
                      newSet.delete(getIdMap[type](i))
                    }
                    setSelectedAssetMap[type](newSet)
                  } else {
                    selectedAssetMap[type].clear()
                    setSelectedAssetMap[type](selectedAssetMap[type])
                  }
                })
              } else {
                gridTypeList.forEach((type, idx) => {
                  const newSet = selectedAssetMap[type]
                  if (idx === 0) {
                    for (let i = 0; i < currentIndex; i++) {
                      newSet.add(getIdMap[type](i))
                    }
                  } else if (idx === gridTypeList.length - 1) {
                    for (let i = newIndex; i < assetCountMap[type]; i++) {
                      newSet.add(getIdMap[type](i))
                    }
                  } else {
                    for (let i = 0; i < assetCountMap[type]; i++) {
                      newSet.add(getIdMap[type](i))
                    }
                  }
                  setSelectedAssetMap[type](newSet)
                })
              }
            }
          } else {
            selectSingleAsset(gridType, newId)
          }
          setFocusedIndex({ index: newIndex, type: gridType, id: newId })
          break
        }
        case 'ArrowDown': {
          event.preventDefault()
          newIndex = currentIndex + colCount
          const gridTypeList: string[] = [gridType]

          while (
            currentGridOrderIndex < gridOrder.length - 1 &&
            newIndex >= assetCountMap[gridType]
          ) {
            currentGridOrderIndex++
            const nextGridType = gridOrder[currentGridOrderIndex]
            const nextGridCount = assetCountMap[nextGridType]
            if (nextGridCount <= 0) continue
            else {
              const currentColumnPosition =
                (currentIndex + 1) % colCount === 0
                  ? colCount
                  : (currentIndex + 1) % colCount
              const firstRowCount =
                nextGridCount >= colCount ? colCount : nextGridCount
              if (firstRowCount >= currentColumnPosition) {
                newIndex = currentColumnPosition - 1
                gridType = nextGridType
              }

              gridTypeList.push(nextGridType)
            }
          }
          if (newIndex >= assetCountMap[gridType]) {
            return
          }
          newId = getIdMap[gridType](newIndex)
          if (event.shiftKey) {
            if (gridTypeList.length === 1) {
              const newSet = selectedAssetMap[gridType]
              if (newSet.has(newId)) {
                for (let i = currentIndex; i < newIndex; i++) {
                  newSet.delete(getIdMap[gridType](i))
                }
              } else {
                for (let i = currentIndex + 1; i <= newIndex; i++) {
                  newSet.add(getIdMap[gridType](i))
                }
              }
              setSelectedAssetMap[gridType](newSet)
            } else {
              const upcomingGrid = selectedAssetMap[gridType]
              if (upcomingGrid.has(newId)) {
                gridTypeList.forEach((type, idx) => {
                  if (idx === 0) {
                    const newSet = selectedAssetMap[type]
                    for (let i = currentIndex; i < assetCountMap[type]; i++) {
                      newSet.delete(getIdMap[type](i))
                    }
                    setSelectedAssetMap[type](newSet)
                  } else if (idx === gridTypeList.length - 1) {
                    const newSet = selectedAssetMap[type]
                    for (let i = 0; i < newIndex; i++) {
                      newSet.delete(getIdMap[type](i))
                    }
                    setSelectedAssetMap[type](newSet)
                  } else {
                    selectedAssetMap[type].clear()
                    setSelectedAssetMap[type](selectedAssetMap[type])
                  }
                })
              } else {
                gridTypeList.forEach((type, idx) => {
                  const newSet = selectedAssetMap[type]
                  if (idx === 0) {
                    for (let i = currentIndex; i < assetCountMap[type]; i++) {
                      newSet.add(getIdMap[type](i))
                    }
                  } else if (idx === gridTypeList.length - 1) {
                    for (let i = 0; i <= newIndex; i++) {
                      newSet.add(getIdMap[type](i))
                    }
                  } else {
                    for (let i = 0; i < assetCountMap[type]; i++) {
                      newSet.add(getIdMap[type](i))
                    }
                  }
                  setSelectedAssetMap[type](newSet)
                })
              }
            }
          } else {
            selectSingleAsset(gridType, newId)
          }
          setFocusedIndex({ index: newIndex, type: gridType, id: newId })
          break
        }
        case 'Enter':
        case ' ': {
          event.preventDefault()
          return
        }
        default:
          return
      }

      // Schedule scroll after DOM/layout updates; ensure focus even if element isn't yet rendered
      const now = Date.now()
      const elapsed = now - lastScrollAtRef.current

      const doScroll = () => {
        if (scheduledScrollRef.current) {
          cancelAnimationFrame(scheduledScrollRef.current)
        }
        scheduledScrollRef.current = requestAnimationFrame(() => {
          const element = document.querySelector(
            `[data-index="${newIndex}"][data-grid-type="${gridType}"]`,
          ) as HTMLElement | null

          if (!element) {
            assetRefMap[gridType]?.current?.scrollToIndex({
              index: newIndex,
              align: 'center',
              behavior: 'auto',
            })
            return
          }

          const rect = element.getBoundingClientRect()
          const isInView =
            rect.top >= 120 &&
            rect.left >= 0 &&
            rect.bottom <=
              (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <=
              (window.innerWidth || document.documentElement.clientWidth)
          if (!isInView) {
            assetRefMap[gridType]?.current?.scrollToIndex({
              index: newIndex,
              align: 'center',
              behavior: 'auto',
            })
          }
        })
      }

      if (elapsed >= throttleMs) {
        lastScrollAtRef.current = now
        doScroll()
      } else {
        const delay = throttleMs - elapsed
        if (pendingTimeoutRef.current) {
          clearTimeout(pendingTimeoutRef.current)
        }
        pendingTimeoutRef.current = window.setTimeout(() => {
          lastScrollAtRef.current = Date.now()
          doScroll()
        }, delay)
      }
    },
    [
      gridOrder,
      assetCountMap,
      getIdMap,
      colCount,
      selectedAssetMap,
      setSelectedAssetMap,
      JSON.stringify(focusedIndex),
      setFocusedIndex,
      assetRefMap,
    ],
  )

  React.useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current)
      if (scheduledScrollRef.current)
        cancelAnimationFrame(scheduledScrollRef.current)
    }
  }, [])

  return handleKeyDown
}

export default useGridKeyboardNavigation
